import { Vec3 } from '@/core/math/Vec3';
import { tangentBasis } from '@/frames/PlanetaryCoordinates';
import { HeightField } from './HeightField';

export interface FootprintSample {
  /** Highest surface radius under any part of the footprint, metres. */
  highestRadius: number;
  /** Lowest surface radius under any part of the footprint, metres. */
  lowestRadius: number;
  /** Peak-to-trough terrain variation across the footprint, metres. */
  relief: number;
  /** Mean surface radius under the footprint, metres. */
  meanRadius: number;
}

export interface LandingSite {
  /** Unit direction, planet-fixed. */
  direction: Vec3;
  /** Radius at which the vessel's origin should sit so all legs just clear. */
  spawnRadius: number;
  /** Terrain variation across the footprint, metres. Lower is better. */
  relief: number;
  /** Whether a site meeting the tolerance was actually found. */
  acceptable: boolean;
  /** True when the chosen site is dry land. */
  onLand: boolean;
}

/**
 * Terrain variation across a vessel's landing footprint.
 *
 * Sampling only the point beneath the centre of mass is not enough. Landing
 * gear spans several metres, and metre-scale terrain routinely varies by more
 * than the gear's travel across that span. A vessel placed using the centre
 * sample alone ends up with one leg buried in a rise, which the contact solver
 * then resolves as a violent ejection.
 */
export function sampleFootprint(
  field: HeightField,
  directionFixed: Vec3,
  contacts: readonly [number, number, number][],
): FootprintSample {
  const dir = directionFixed.normalized();
  const basis = tangentBasis(dir);
  const centre = field.walkableRadiusAt(dir);

  let highest = centre;
  let lowest = centre;
  let sum = centre;
  let count = 1;

  for (const c of contacts) {
    const offset = basis.east.clone().mulScalar(c[0]).addScaled(basis.north, c[2]);
    const sampleDir = dir.clone().mulScalar(field.radius).add(offset).normalize();
    const r = field.walkableRadiusAt(sampleDir);
    highest = Math.max(highest, r);
    lowest = Math.min(lowest, r);
    sum += r;
    count++;
  }

  return {
    highestRadius: highest,
    lowestRadius: lowest,
    relief: highest - lowest,
    meanRadius: sum / count,
  };
}

/**
 * Offsets a unit direction along a great circle.
 *
 * Tangent-plane offsets are fine over a few kilometres, but the coarse land
 * search below covers thousands, where the tangent plane has diverged from the
 * sphere badly enough to distort the search pattern. Rotating within the plane
 * spanned by the direction and a tangent bearing keeps the angular step exact at
 * any distance.
 */
function greatCircleOffset(
  direction: Vec3,
  basis: { east: Vec3; north: Vec3 },
  angle: number,
  bearing: number,
): Vec3 {
  const tangent = basis.east
    .clone()
    .mulScalar(Math.cos(bearing))
    .addScaled(basis.north, Math.sin(bearing));
  return direction.clone().mulScalar(Math.cos(angle)).addScaled(tangent, Math.sin(angle)).normalize();
}

/**
 * Finds the closest dry land to a direction that falls in open water.
 *
 * Authored coordinates — spaceports, settlements, mission targets — are written
 * against a map nobody has seen yet: the terrain comes from the world seed, so a
 * plausible-looking latitude and longitude can land in the middle of an ocean.
 * Rather than treat that as authoring error, the world snaps such a coordinate
 * to the nearest coastline. The spiral's radius grows monotonically with the
 * sample index, so the first land hit is also the nearest one in the sampled
 * set, and the search is deterministic: the same seed always puts the same
 * settlement in the same place.
 *
 * @param maxAngle widest arc to search, radians of great-circle distance
 */
function findNearestLand(field: HeightField, direction: Vec3, maxAngle: number, samples: number): Vec3 | null {
  const basis = tangentBasis(direction);
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 1; i <= samples; i++) {
    const angle = maxAngle * Math.sqrt(i / samples);
    const candidate = greatCircleOffset(direction, basis, angle, i * golden);
    if (!field.isOverWater(candidate)) return candidate;
  }
  return null;
}

/**
 * Searches for the flattest ground near a nominal position.
 *
 * This is what siting a landing pad actually involves: you do not set down on
 * whatever happens to be under the nominal coordinate, you look around for
 * ground the gear can cope with. The search is a deterministic spiral, so the
 * same nominal position always resolves to the same site — settlements and
 * saved bases stay where they were.
 *
 * With `requireLand`, the search runs in two stages: a coarse great-circle scan
 * that finds a coastline anywhere on the planet, then the ordinary local scan
 * for level ground around it. The two are separate because they answer different
 * questions at wildly different scales — "which continent" and "which hillside".
 *
 * @param tolerance acceptable terrain variation across the footprint, metres
 */
export function findLevelSite(
  field: HeightField,
  nominalDirection: Vec3,
  contacts: readonly [number, number, number][],
  options: {
    searchRadius?: number;
    samples?: number;
    tolerance?: number;
    clearance?: number;
    /** Reject sites over water. Used for settlements and permanent bases. */
    requireLand?: boolean;
    /** Widest arc the coarse land search may cover, radians. */
    landSearchAngle?: number;
    /** Samples in the coarse land search. */
    landSearchSamples?: number;
  } = {},
): LandingSite {
  const searchRadius = options.searchRadius ?? 900;
  const samples = options.samples ?? 48;
  const tolerance = options.tolerance ?? 1.2;
  const clearance = options.clearance ?? 0.6;
  const requireLand = options.requireLand ?? false;
  // Half a radian is roughly 3000 km on an Earth-sized world: far enough to
  // reach a continent from mid-ocean, near enough that a coastal coordinate
  // still resolves to its own coast.
  const landSearchAngle = options.landSearchAngle ?? 0.55;
  const landSearchSamples = options.landSearchSamples ?? 1024;

  let dir = nominalDirection.normalized();
  if (requireLand && field.isOverWater(dir)) {
    const land = findNearestLand(field, dir, landSearchAngle, landSearchSamples);
    if (land) dir = land;
  }
  const basis = tangentBasis(dir);
  let lowestContact = 0;
  for (const c of contacts) lowestContact = Math.min(lowestContact, c[1]);

  const wet = (d: Vec3): boolean => field.isOverWater(d);

  let bestDirection = dir;
  let bestSample = sampleFootprint(field, dir, contacts);
  let bestOnLand = !wet(dir);
  // A dry but rough site always beats a perfectly flat sea surface.
  let satisfied = bestSample.relief <= tolerance && (!requireLand || bestOnLand);

  if (!satisfied) {
    // Golden-angle spiral: even coverage of the search disc with no clustering,
    // and no random numbers, so the result is reproducible. The coarse land
    // search has already put `dir` ashore if it needed to, so this stage only
    // has to find level ground within walking distance of it.
    const golden = Math.PI * (3 - Math.sqrt(5));
    const effectiveSamples = samples;

    for (let i = 1; i <= effectiveSamples; i++) {
      const radius = searchRadius * Math.sqrt(i / effectiveSamples);
      const angle = i * golden;
      const offset = basis.east
        .clone()
        .mulScalar(Math.cos(angle) * radius)
        .addScaled(basis.north, Math.sin(angle) * radius);
      const candidate = dir.clone().mulScalar(field.radius).add(offset).normalize();
      const candidateOnLand = !wet(candidate);

      // Land beats water outright; among equals, flatter wins.
      if (requireLand && bestOnLand && !candidateOnLand) continue;
      const upgradesToLand = requireLand && !bestOnLand && candidateOnLand;
      const sample = sampleFootprint(field, candidate, contacts);
      if (!upgradesToLand && sample.relief >= bestSample.relief) continue;

      bestSample = sample;
      bestDirection = candidate;
      bestOnLand = candidateOnLand;
      if (sample.relief <= tolerance && (!requireLand || bestOnLand)) break;
    }
  }

  return {
    direction: bestDirection,
    // Sit on the highest ground under the footprint so no leg starts buried.
    spawnRadius: bestSample.highestRadius + Math.abs(lowestContact) + clearance,
    relief: bestSample.relief,
    acceptable: bestSample.relief <= tolerance && (!requireLand || bestOnLand),
    onLand: bestOnLand,
  };
}
